import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AssetPropertiesLoaderService {
  private regionPropertiesLoaded = new Map<string, BehaviorSubject<boolean>>();

  constructor() { }

  /**
   * Get the properties loaded state for a specific region
   * @param regionId The region ID
   * @returns Observable<boolean> that emits the loaded state
   */
  public getRegionPropertiesLoaded(regionId: string): Observable<boolean> {
    if (!this.regionPropertiesLoaded.has(regionId)) {
      this.regionPropertiesLoaded.set(regionId, new BehaviorSubject<boolean>(false));
    }
    return this.regionPropertiesLoaded.get(regionId).asObservable();
  }

  /**
   * Set the properties loaded state for a specific region
   * @param regionId The region ID
   * @param loaded Whether properties are loaded
   */
  public setRegionPropertiesLoaded(regionId: string, loaded: boolean): void {
    if (!this.regionPropertiesLoaded.has(regionId)) {
      this.regionPropertiesLoaded.set(regionId, new BehaviorSubject<boolean>(loaded));
    } else {
      this.regionPropertiesLoaded.get(regionId).next(loaded);
    }
  }

  /**
   * Get the current properties loaded state for a specific region
   * @param regionId The region ID
   * @returns boolean indicating if properties are loaded
   */
  public isRegionPropertiesLoaded(regionId: string): boolean {
    if (!this.regionPropertiesLoaded.has(regionId)) {
      return false;
    }
    return this.regionPropertiesLoaded.get(regionId).value;
  }

  /**
   * Clear the properties loaded state for a specific region
   * @param regionId The region ID
   */
  public clearRegionProperties(regionId: string): void {
    if (this.regionPropertiesLoaded.has(regionId)) {
      this.regionPropertiesLoaded.get(regionId).next(false);
    }
  }

  /**
   * Clear all region properties loaded states
   */
  public clearAllRegionProperties(): void {
    this.regionPropertiesLoaded.forEach(subject => subject.next(false));
  }

  public waitUntilReady(regionId: string): Promise<void> {
    if (this.isRegionPropertiesLoaded(regionId)) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const subscription = this.getRegionPropertiesLoaded(regionId).subscribe((loaded) => {
        if (loaded) {
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }
}

